import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getECardSrc } from '@components/ECard';
import { CTALink } from '@components/CTALink';
import { Box } from '@components/Box';
import { JoykitColor } from '@components/themes';
import ReactHtmlParser from 'react-html-parser';
import {
  ColumnsWrapper,
  FlipCard,
  FlipCardWrapper,
  ImageCard,
  ImageColumn,
  MainTextFlex,
  MainTextPairing,
  MainTextWrapper,
  SaveDatesImage,
  TemplateShadow,
  TextColumn,
} from './PersonalSaveTheDatesSection.styles';
import { gsap } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';

interface PersonalSaveTheDatesSectionProps
  extends Readonly<{
    sectionId: string;
    backgroundColorPalette?: string;
    colorPalette?: string;
    isImageLeft?: boolean;
    shadowColorPalette?: string;
    textData: {
      templateAltText1: string;
      templateAltText2: string;
      main: {
        eyebrow: string;
        title: string;
        content: string;
      };
      ctaText: string;
    };
  }> {}

const saveTheDateImageOne = getECardSrc('goldfoil');
const saveTheDateImageTwo = getECardSrc('blueacrylic');
const saveTheDateImageThree = getECardSrc('botanicalfun');

export const PersonalSaveTheDatesSection: React.FC<PersonalSaveTheDatesSectionProps> = ({
  backgroundColorPalette,
  colorPalette,
  isImageLeft,
  textData,
  shadowColorPalette,
  sectionId,
}) => {
  const flipCardRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const isMobileDevice = useThemeMediaQuery('below_sm');
  const [currentImage, setCurrentImage] = useState(saveTheDateImageOne);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    setCurrentImage(isFlipped ? saveTheDateImageThree : saveTheDateImageOne);
  }, [isFlipped]);

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let flipCardTimeline: ReturnType<typeof gsap.timeline> | null;
    if (scrollController && isMounted) {
      let direction = 'FORWARD';
      const initTimeline = () => {
        const flipCardEl = flipCardRef.current;
        if (scrollController && flipCardEl) {
          flipCardTimeline = gsap.timeline({ paused: true });
          flipCardTimeline
            .to(flipCardEl, {
              rotationY: -180,
              duration: 1.8,
            })
            .call(() => {
              if (direction === 'FORWARD') {
                setIsFlipped(true);
              } else {
                setIsFlipped(false);
              }
            }, [])
            .to(flipCardEl, {
              rotationY: -225,
              duration: 0.45,
            })
            .to(flipCardEl, {
              rotationY: -360,
              duration: 1.35,
            });
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: `#${sectionId}`,
          triggerHook: 0.4,
          duration: '60%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            direction = event.scrollDirection;
            flipCardTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (flipCardTimeline) {
        flipCardTimeline.progress(0);
        flipCardTimeline.kill();
        flipCardTimeline = null;
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, flipCardRef, isMobileDevice, sectionId]);

  const backgroundColor = `${backgroundColorPalette}1` as JoykitColor;
  const shadowColor = `${shadowColorPalette || backgroundColorPalette}3` as JoykitColor;
  const eyebrowColor = `${colorPalette}5` as JoykitColor;
  const titleColor = `${colorPalette}6` as JoykitColor;

  return (
    <Box as={'section'} backgroundColor={backgroundColor} paddingTop={[10, 12]} paddingBottom={[10, 12]} id={sectionId}>
      <ColumnsWrapper>
        <SaveDatesImage>
          <ImageColumn width={6} isImageLeft={isImageLeft}>
            <FlipCardWrapper>
              <FlipCard ref={flipCardRef}>
                {/* TODO: this should be changed to ECard component*/}
                <TemplateShadow backgroundColor={backgroundColor} shadowColor={shadowColor} />
                <ImageCard src={currentImage} alt={textData.templateAltText1} />
                <ImageCard src="common/save-the-date-text/std-msg-3.svg" alt="" $isTextCard={true} />
                <ImageCard src={saveTheDateImageTwo} alt={textData.templateAltText2} $isBackFace={true} />
                <ImageCard src="common/save-the-date-text/std-msg-3.svg" alt="" $isBackFace={true} $isTextCard={true} />
              </FlipCard>
            </FlipCardWrapper>
          </ImageColumn>
          <TextColumn width={6}>
            <MainTextWrapper>
              <MainTextFlex flexDirection={'column'} alignItems={['center', 'flex-start']} isImageLeft={isImageLeft}>
                <div>
                  <MainTextPairing
                    eyebrow={textData.main.eyebrow}
                    eyebrowTagName="h2"
                    eyebrowVariant={'sectionEyeBrow24'}
                    eyebrowColor={eyebrowColor}
                    title={ReactHtmlParser(textData.main.title)}
                    titleVariant={'hed56'}
                    titleColor={titleColor}
                    titleTagName="h2"
                    body={textData.main.content}
                    marginBottom={6}
                  />
                  <CTALink id="PersonalSaveTheDatesLearnMore" href="/cards/wedding/save-the-date" hasChevron>
                    {textData.ctaText}
                  </CTALink>
                </div>
              </MainTextFlex>
            </MainTextWrapper>
          </TextColumn>
        </SaveDatesImage>
      </ColumnsWrapper>
    </Box>
  );
};
